import React from "react";
import { Pen, SaveAll, Undo } from "lucide-react";
import { TrashBinIcon } from "assets/icons";

export type ActionCellRendererProps = {
  params: any; // Using any for flexibility with AG Grid params
  editingRowId: number | null;
  enableInlineEdit: boolean;
  onSave?: (data: any, meta?: { pinned?: boolean }) => void;
  onEdit?: (params: any) => void;
  onDelete?: (data: any) => void;
  onAfterActions?: () => void;
  clearRowDirty: (rowId?: string) => void;
  resolveRowId: (
    data: any,
    nodeId?: string,
    rowIndex?: number,
    pinned?: string | null,
  ) => string | undefined;
  setEditingRowId: React.Dispatch<React.SetStateAction<number | null>>;
};

const ActionCellRenderer = React.memo(
  ({
    params,
    editingRowId,
    enableInlineEdit,
    onSave,
    onEdit,
    onDelete,
    onAfterActions,
    clearRowDirty,
    resolveRowId,
    setEditingRowId,
  }: ActionCellRendererProps) => {
    if (params.node?.rowPinned && !onSave) return null;

    const data = params.data;
    const pinned = !!params.node?.rowPinned;
    const isEditing =
      pinned || (data?.id !== undefined && data.id === editingRowId);

    const rowId = resolveRowId(
      data,
      params.node?.id,
      params.node?.rowIndex ?? undefined,
      params.node?.rowPinned,
    );

    const handleStopEdit = () => params.api?.stopEditing(false);

    const handleCancel = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleStopEdit();
      onAfterActions?.();
      setEditingRowId(null);
    };

    const handleSave = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      handleStopEdit();
      onSave?.(data, { pinned });
      clearRowDirty(rowId);
    };

    const handleEdit = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onEdit?.(params);
      if (enableInlineEdit && data?.id !== undefined) {
        setEditingRowId(data.id as number);
      }
    };

    const handleDelete = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onDelete?.(data);
      clearRowDirty(rowId);
    };

    return (
      <div className="flex gap-2 h-full items-center justify-center">
        {/* Cancel button - show when editing */}
        {enableInlineEdit && isEditing && (
          <button
            className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 text-xs font-semibold"
            title="Hủy"
            onClick={handleCancel}
          >
            <Undo className="w-4 h-4" />
          </button>
        )}

        {/* Save button - show when editing */}
        {enableInlineEdit && isEditing && (
          <button
            className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 text-xs font-semibold"
            title="Lưu"
            onClick={handleSave}
          >
            <SaveAll className="w-4 h-4" />
          </button>
        )}

        {/* Edit button - show when not pinned and not editing */}
        {!pinned && !isEditing && onEdit && (
          <button
            className="text-blue-600 hover:text-blue-700 text-xs font-semibold"
            onClick={handleEdit}
          >
            <Pen className="w-3 h-3" />
          </button>
        )}

        {/* Delete button - show when not pinned and not editing */}
        {onDelete && !pinned && !isEditing && (
          <button
            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-semibold"
            onClick={handleDelete}
          >
            <TrashBinIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  },
);

ActionCellRenderer.displayName = "ActionCellRenderer";

export default ActionCellRenderer;
